import React, { useEffect } from 'react';
import { DatePicker, notification, Select } from 'antd';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from 'constants/dates';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'components/customAntd/Loader';
import { fetchLocations } from 'store/slices/locationsSlice';
import { useTranslation } from 'react-i18next';

interface AdvancedSearchModalProps {
    open: boolean;
    onCancel: () => void;
    onSearch: (values: any) => void;
    searchLoading: boolean;
}

const { RangePicker } = DatePicker;

const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
    open,
    onCancel,
    onSearch,
    searchLoading
}) => {
    const dispatch: AppDispatch = useDispatch();
    const [form] = OpForm.useForm();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const defaultLocationId = useSelector((state: RootState) => state.users.selectedUserSites?.[0]?.id);
    const { locations, fetchLocationsLoading } = useSelector((state: RootState) => state.locations);
    const { t } = useTranslation();

    // Fetch locations
    useEffect(() => {
        if (orgId && locations.length === 0) {
            dispatch(fetchLocations({ orgId, status: 1 }));
        }
    }, [dispatch, orgId, locations.length]);

    useEffect(() => {
        if (open && locations.length > 0) {
            const defaultSiteId = defaultLocationId ?? (locations.length > 1 ? -1 : locations[0].id);
            form.setFieldsValue({ siteId: defaultSiteId });
        }
    }, [open, locations, defaultLocationId, form]);

    const handleFinish = (values: any) => {
        const { firstName, lastName, dateRange } = values.values;

        // Add notification check
        if (!firstName && !lastName) {
            notification.error({
                message: t('notification.error'),
                description: t('appHeader.advancedSearch.searchError'),
                placement: 'bottomRight',
            });
            return;
        }

        const processedValues = {
            ...values.values,
            startDate: dateRange?.[0]?.startOf('day').format(DATE_TIME_FORMAT),
            endDate: dateRange?.[1]?.endOf('day').format(DATE_TIME_FORMAT),
        };
        onSearch(processedValues);
        form.resetFields();
    };

    return (
        <OpModal
            title={t('appHeader.advancedSearch.title')}
            open={open}
            onOk={() => form.submit()}
            onCancel={onCancel}
            okText={t('appHeader.advancedSearch.search')}
            cancelText={t('appHeader.advancedSearch.cancel')}
        >
            {fetchLocationsLoading || searchLoading ? (
                <Loader />
            ) : (
                <OpForm
                    form={form}
                    onSubmit={handleFinish}
                    hasError={false}
                    defaultButtons={false}
                >
                    <OpForm.Input
                        label={t('appHeader.advancedSearch.firstName')}
                        name="firstName"
                    />

                    <OpForm.Input
                        label={t('appHeader.advancedSearch.lastName')}
                        name="lastName"
                    />

                    <OpForm.Input
                        label={t('appHeader.advancedSearch.middleName')}
                        name="middleName"
                    />

                    <OpForm.Item
                        name="siteId"
                        label={t('appHeader.advancedSearch.location')}
                    >
                        <Select
                            options={
                                locations.length > 1
                                    ? [
                                        { label: t('appHeader.advancedSearch.allLocations'), value: -1 },
                                        ...locations.map(location => ({
                                            label: location.name,
                                            value: location.id,
                                        })),
                                    ]
                                    : locations.map(location => ({
                                        label: location.name,
                                        value: location.id,
                                    }))
                            }
                        />
                    </OpForm.Item>

                    <OpForm.Item label={t('appHeader.advancedSearch.dateRange')} name="dateRange">
                        <RangePicker
                            placeholder={[t('appHeader.advancedSearch.startDate'), t('appHeader.advancedSearch.endDate')]}
                            presets={[
                                { label: t('appHeader.advancedSearch.last7Days'), value: [dayjs().subtract(7, 'd'), dayjs()] },
                                { label: t('appHeader.advancedSearch.last30Days'), value: [dayjs().subtract(30, 'd'), dayjs()] },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </OpForm.Item>
                </OpForm>
            )}
        </OpModal>
    );
};

export default AdvancedSearchModal;